// Shared cookie and blue/green environment helpers
// Centralizes previously duplicated logic found in useCustomQuery and Analytics components.

export const getCookieValue = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';')?.shift() || null;
  return null;
};

// Reads bgEnv from the current URL query string.
// Only returns allowed values to prevent arbitrary input.
export const getBgEnvFromUrl = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const bgEnv = params.get('bgEnv');
    if (bgEnv && ['blue', 'green', 'live'].includes(bgEnv)) return bgEnv;
  } catch {
    // Ignore URL parsing errors
  }
  return null;
};

// Checks whether the host exactly matches or is a subdomain of the given domain.
// Enforces a dot boundary so 'eviluhc.com' does NOT match 'uhc.com'.
// Uses URL parsing so static analysers (CodeQL) can verify the check.
export const isHostOfDomain = (host: string, domain: string): boolean => {
  try {
    // Construct a URL to let the browser normalise the hostname.
    const { hostname } = new URL(`https://${host}`);
    return hostname === domain || hostname.endsWith(`.${domain}`);
  } catch {
    return false;
  }
};

// Determines the blue/green environment override based on host + cookies/URL.
// Returns null if not applicable.
export const getBlueGreenEnv = (
  host: string,
  isWidget?: boolean
): string | null => {
  if (!host) return null;
  const allowedDomains = ['uhc.com', 'tpa.com', 'liveandworkwell.com'];
  const isAllowedHost = allowedDomains.some((domain) =>
    isHostOfDomain(host, domain)
  );
  if (!isAllowedHost) return null;
  // Widgets should not send bgEnv (previous behavior preserved)
  if (isWidget) return null;

  // For liveandworkwell.com (LAWW authenticated flow), bgEnv comes via URL
  // param on initial load. We persist it to a cookie so it survives SPA
  // navigation where URL params may be stripped.
  if (isHostOfDomain(host, 'liveandworkwell.com')) {
    const urlValue = getBgEnvFromUrl();
    if (urlValue) {
      document.cookie = `bgTestingEnv=${urlValue}; path=/;`;
      document.cookie = `bgParcelsCookie=${urlValue}; path=/;`;
    }
    return urlValue;
  }

  const override = getCookieValue('bgTestingEnv');
  if (
    override &&
    !['null', 'undefined', ''].includes(override.toLowerCase().trim())
  )
    return override;
  const bgParcelsCookie = getCookieValue('bgParcelsCookie');
  if (
    bgParcelsCookie &&
    !['null', 'undefined', ''].includes(bgParcelsCookie.toLowerCase().trim())
  )
    return bgParcelsCookie;

  return null;
};
